import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import TiptapEditor from '../../../components/TiptapEditor/TiptapEditor';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { contentService, ArticleCategory, CreateArticleDto, UpdateArticleDto } from '../../../services/content.service';
import { tenantService } from '../../../services/tenant.service';
import type { Tenant } from '../../../services/tenant.service';
import { CommonState } from '@erpgold/shared';

const { Title } = Typography;

const flattenCategories = (cats: ArticleCategory[], prefix = ''): { label: string; value: string }[] => {
  const result: { label: string; value: string }[] = [];
  for (const cat of cats) {
    result.push({ label: `${prefix}${cat.name}`, value: cat.category_id || cat._id });
    if (cat.children && cat.children.length > 0) {
      result.push(...flattenCategories(cat.children, `${prefix}-- `));
    }
  }
  return result;
};

export const ArticleForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<ArticleCategory[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const isEdit = Boolean(id);

  useEffect(() => {
    contentService.articleCategories.getAll()
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => { });
    tenantService.getTenants({ limit: 100 })
      .then((res) => setTenants(res.data))
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    contentService.articles.getById(id)
      .then((article) => {
        form.setFieldsValue({
          title: article.title,
          sapo: article.sapo,
          category_id: (article.category as ArticleCategory)?.category_id || (article.category as ArticleCategory)?._id,
          description: article.description,
          tags: article.tags,
          state: article.state,
        });
      })
      .catch(() => message.error('Không tải được bài viết'))
      .finally(() => setLoading(false));
  }, [id, form]);

  const handleSubmit = async (values: CreateArticleDto & UpdateArticleDto) => {
    setSaving(true);
    try {
      if (isEdit && id) {
        await contentService.articles.update(id, values);
        message.success('Cập nhật bài viết thành công');
      } else {
        await contentService.articles.create(values);
        message.success('Tạo bài viết thành công');
      }
      navigate('/content/articles');
    } catch (error: unknown) {
      message.error((error as { message?: string })?.message || (isEdit ? 'Cập nhật thất bại' : 'Tạo thất bại'));
    } finally {
      setSaving(false);
    }
  };

  const categoryOptions = flattenCategories(categories);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/content/articles')}>
            Quay lại
          </Button>
          <Title level={4} style={{ margin: 0 }}>
            {isEdit ? 'Sửa bài viết' : 'Tạo bài viết mới'}
          </Title>
        </Space>
      </div>

      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={24}>
            <Col span={16}>
              <Card title="Nội dung" style={{ marginBottom: 16 }}>
                <Form.Item name="title" label="Tiêu đề" rules={[{ required: true, message: 'Vui lòng nhập tiêu đề' }]}>
                  <Input placeholder="Nhập tiêu đề bài viết" size="large" />
                </Form.Item>

                <Form.Item name="sapo" label="Sapo">
                  <Input.TextArea rows={3} placeholder="Mô tả ngắn" />
                </Form.Item>

                <Form.Item name="description" label="Nội dung" rules={[{ required: true, message: 'Vui lòng nhập nội dung' }]}>
                  <TiptapEditor placeholder="Nội dung đầy đủ" />
                </Form.Item>
              </Card>
            </Col>

            <Col span={8}>
              <Card title="Cài đặt" style={{ marginBottom: 16 }}>
                {!isEdit && (
                  <Form.Item name="tenant_id" label="Tenant" rules={[{ required: true, message: 'Vui lòng chọn tenant' }]}>
                    <Select
                      showSearch
                      placeholder="Chọn tenant"
                      options={tenants.map((t) => ({ label: `${t.name} (${t.id})`, value: t.id }))}
                    />
                  </Form.Item>
                )}

                <Form.Item name="state" label="Trạng thái">
                  <Select>
                    <Select.Option value={CommonState.ENABLED}>Hiển thị</Select.Option>
                    <Select.Option value={CommonState.DISABLED}>Ẩn</Select.Option>
                  </Select>
                </Form.Item>

                <Form.Item
                  name="category_id"
                  label="Danh mục"
                  rules={[{ required: true, message: 'Vui lòng chọn danh mục' }]}
                >
                  <Select placeholder="Chọn danh mục" showSearch options={categoryOptions} />
                </Form.Item>

                <Form.Item name="tags" label="Tags">
                  <Select mode="tags" placeholder="Nhập tags" style={{ width: '100%' }} />
                </Form.Item>
              </Card>

              <Card>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    htmlType="submit"
                    loading={saving}
                    block
                    size="large"
                  >
                    {isEdit ? 'Lưu thay đổi' : 'Tạo bài viết'}
                  </Button>
                  <Button block onClick={() => navigate('/content/articles')}>
                    Hủy
                  </Button>
                </Space>
              </Card>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

export default ArticleForm;
